
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
    import { ImpferinnerungenFilter, ImpferinnerungenPagination, ValidationRules } from '@/store/types';
    import {
        AlsErinnertMarkierenPayload,
        LoadImpferinnerungenPayload,
        LoadKarteiImpfungenPayload, PrintBriefePayload, SendEmailPayload,
    } from '@/store/impferinnerungen';
    import { CompareFunction, DataTableHeaders } from '@/types';
    import { confirmSwal } from '@/helper/swals';
    import { SweetAlertResult } from 'sweetalert2';
    import Beleg from '@/types/Beleg';
    import Impfstelle from '@/types/Impfstelle';
    import moment from 'moment';
    import RechnungserinnerungsbriefDetail from '@/components/Rechnungseingang/RechnungserinnerungsbriefDetail.vue';
    import {AlsGemanhtMarkierenPayload, ZahlungserinnerungenOptions} from '@/store/zahlungserinnerungen';
    import {VMenu} from '@/types/Components';

    @Component({
        components: {
            RechnungserinnerungsbriefDetail,
        },
        computed: {
            ...mapState('globals', ['formatDate', 'parseDate', 'rules', 'sortDate']),
            ...mapState('zahlungserinnerungen', [
                'selectedItems',
                'filter',
                'loaders',
                'dialogs',
                'brief',
                'totalCount',
                'offeneZahlscheinBelege'
            ]),
            ...mapGetters('authentication', ['impfstelleDetail']),
            ...mapState('globals', ['sortDate']),
            ...mapState('authentication', ['impfstelle_id']),
            ...mapState('kassafuehrung', ['beleg']),
            ...mapState('authentication', ['impfstelle']),
            ...mapState('zahlungserinnerungen', {offeneBelegeLoaders: 'loaders' }),
        },
        methods: {
            ...mapActions('zahlungserinnerungen', ['loadOffeneZahlscheinBelege']),
            ...mapMutations('kassafuehrung', ['setDialogZahlungseingang', 'setBeleg']),
            ...mapActions('authentication', ['loadImpfstelle']),
            ...mapActions('zahlungserinnerungen', [
                'setSelectedItems',
                'setBrief',
                'setDialogs',
                'openBrief',
                'printBriefe',
                'sendEmail',
                'alsGemahntMarkieren',
            ]),
        },
    })
    export default class Zahlungserinnerungen extends Vue {
        public selectedItems!: Beleg[];
        public rules!: ValidationRules;
        public parseDate!: (input: string | null) => string | null;
        public formatDate!: (input: string | null) => string | null;
        public setBrief!: (beleg: Beleg) => void;
        public openBrief!: (beleg_id: number | any) => void;
        public setDialogs!: (payload: { brief?: boolean, kartei?: boolean }) => void;
        public printBriefe!: (payload: PrintBriefePayload) => void;
        public sendEmail!: (payload: SendEmailPayload) => void;
        public alsGemahntMarkieren!: (payload: AlsGemanhtMarkierenPayload) => void;
        public setSelectedItems!: (items: Beleg[]) => void;
        public sortDate!: CompareFunction<Beleg>;
        public loadOffeneZahlscheinBelege!: (payload: any) => void ;
        public setBeleg!: (payload: { beleg: Beleg, load?: boolean }) => void;
        public setDialogZahlungseingang!: (payload: { zahlungseingang?: boolean}) => void;
        public offeneZahlscheinBelege!: Beleg[];
        public impfstelleDetail!: Impfstelle;
        public impfstelle_id!: number;
        public beleg: any;
        public loadImpfstelle!: () => void;
        public impfstelle!: Impfstelle;
        public zeitraumMenu = false;
        public zeitraumErrorMessages: string[] = [];
        public options: ZahlungserinnerungenOptions = {
            zeitraum: [
                moment().subtract(100, 'days').format('YYYY-MM-DD'),
                moment().format('YYYY-MM-DD'),
            ],
            zahlungsziel_tage: 0,
            briefe: [],
        };

        public $refs!: {
            zeitraumMenu: VMenu;
        };

        public get maxZeitraum(): string {
            return moment().format('YYYY-MM-DD');
        }

        public get zeitraumLabel(): string {
            return this.options.zeitraum.length
                ? 'von ' + this.options.zeitraum.sort((a, b) => a.localeCompare(b)).map((date) => {
                    const [year, month, day] = date.split('-');
                    return `${day}.${month}.${year}`;
                }).join(' bis ')
                : '';
        }

        public saveZeitraum(date: [string, string]): void {
            this.options.zeitraum = date;
            this.$refs.zeitraumMenu.save(date);

            this.zeitraumErrorMessages = [];

            const [von, bis] = this.options.zeitraum;
            if (Math.abs(moment(von).diff(moment(bis), 'days')) > 365) {
                this.zeitraumErrorMessages.push('Zeitraum darf nicht mehr als 365 Tage überschreiten.');
            }
        }

        public get selected(): Beleg[] {
            return this.selectedItems;
        }

        public set selected(items: Beleg[]) {
            this.setSelectedItems(items);
        }

        public get briefe(): number[] {
            return this.selected.map((beleg: Beleg) => beleg.beleg_id as number);
        }

        public get headers(): DataTableHeaders<Beleg> {
            return [
                { value: 'belegnr', text: 'Beleg Nr.', align: 'center'},
                { value: 'datum', text: 'Datum', sort: this.sortDate },
                { value: 'belegempfaenger', text: 'Belegempfänger' },
                { value: 'gesamtbetrag', text: 'Rechnungsbetrag'},
                { value: 'zahlungsziel', text: 'Zahlungziel'},
                { value: 'gemahnt', text: 'Gemahnt am'},
                { value: 'actions', text: '', sortable: false, width: 50 },
            ];
        }

        public mounted(): void {
            this.loadImpfstelle();
            this.options.zahlungsziel_tage = this.impfstelle.zahlungsziel_tage;
            this.loadOffeneZahlscheinBelege(this.options);
        }

        public onCloseBrief(): void {
            this.setBrief(new Beleg());
            this.setDialogs({ brief: false });
        }

        public onClickPrint(): void {
            const briefe = this.briefe;
            const werden = briefe.length === 1 ? 'wird' : 'werden';
            const e = (array: Array<unknown>): string => array.length === 1 ? '' : 'e';

            confirmSwal(
                'Drucken',
                `Es ${werden} ${briefe.length} Brief${e(briefe)}` +
                    ' gedruckt. Sind Sie sicher?',
                'Drucken',
                'Abbrechen',
            ).then(({ isConfirmed }: SweetAlertResult) => {
                if (isConfirmed) {
                    this.printBriefe({ briefe });
                }
            });
        }

        public onClickEmail(): void {
            const briefe = this.briefe;
            const werden = briefe.length === 1 ? 'wird' : 'werden';
            const s = (array: Array<unknown>): string => array.length === 1 ? '' : 's';

            confirmSwal(
                'E-Mail',
                `Es ${werden} ${briefe.length} E-Mail${s(briefe)}` +
                    ' versendet. Sind Sie sicher?',
                'Senden',
                'Abbrechen',
            ).then(({ isConfirmed }: SweetAlertResult) => {
                if (isConfirmed) {
                    this.sendEmail({ briefe });
                }
            });
        }

        public onClickGemahnt(): void {
            const impfungen = this.selected;
            const werden = impfungen.length === 1 ? 'wird' : 'werden';
            const en = (array: Array<unknown>): string => array.length === 1 ? '' : 'en';
            confirmSwal(
                'Gemahnt',
                `Es ${werden} ${impfungen.length} Impfung${en(impfungen)} als gemahnt` +
                    ' markiert. Sind Sie sicher?',
                'Gemahnt',
                'Abbrechen',
            ).then(({ isConfirmed }: SweetAlertResult) => {
                if (isConfirmed) {
                    this.options.briefe = this.briefe;
                    this.alsGemahntMarkieren(this.options);
                }
            });
        }

        public getZahlungsziel(item: string): string | any {
            var zahlungziel = moment(item).add(this.impfstelle.zahlungsziel_tage, 'days').format('YYYY-MM-DD');
            return this.$d(zahlungziel);
        }

        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        public onClickOpenBrief(item: Beleg) {
            this.setBeleg({beleg: item});
            this.openBrief({ beleg_id: item.beleg_id});
        }

        public onClickFiltern():void {
            this.loadOffeneZahlscheinBelege(this.options);
        }
    }
